import { formatPriceLogistics, getPriceLogisticsLabel } from 'modules/utils/numbers/formatPriceLogistics'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import { TableTextLink } from 'views/ui/TableTextLink/TableTextLink'
import { formatTarif } from 'modules/utils/numbers/formatTarif'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { FC, Fragment, ReactNode } from 'react'
import useFormatDate from 'hooks/useFormatDate'
import BidRoutes from 'views/pages/Bid/routes'

interface Props {
  changes: Record<string, any>
}

export const HistoryChangesCell: FC<Props> = ({ changes }) => {
  const { t } = useTranslation('deal')
  const formatDate = useFormatDate()

  const formatLink = (id: string) => (
    <TableTextLink to={generatePath(BidRoutes.Details, { id })} id="bid" target="_blank">
      {t('rematch.newContract')}
    </TableTextLink>
  )

  const formatters: Record<string, (value: any) => ReactNode> = {
    sale_price: (v) => `${t('common:salePrice')}: ${formatTarif(v)}`,
    purchase_price: (v) => `${t('common:purchasePrice')}: ${formatTarif(v)}`,
    price_carrier: (v) => `${t('fields.carrier_price')}: ${formatPriceLogistics(v)}`,
    price_logistics: (v) => `${getPriceLogisticsLabel()}: ${formatPriceLogistics(v)}`,
    price_logistics_with_overload: (v) => `${t('fields.price_logistics_with_overload')}: ${formatPriceLogistics(v)}`,
    railway_price_logistics: (v) => `${t('fields.railway_price_logistics')}: ${formatPriceLogistics(v)}`,
    quantity: (v) => `${t('common:volume')}: ${formatQuantity(v)}`,
    status: (v) => `${t('common:status')}: ${t(`statuses.${v}`)}`,
    approval_status: (v) => `${t(`approvalStatuses.${v}`)}`,
    executors: () => t('common:assignees'),
    sale_bid: formatLink,
    purchase_bid: formatLink,
    trip_count_norm_calendar: (v) => {
      if (v.unloading_count) {
        return t('tripCountNormCalendarUnloadingHistory', {
          date: formatDate(v.norm_date),
          count: v.unloading_count,
        })
      }
      return t('tripCountNormCalendarLoadingHistory', {
        date: formatDate(v.norm_date),
        count: v.loading_count,
      })
    },
    unloading_count: (v) => t('countPerDayHistoryUnloading', { count: v }),
    loading_count: (v) => t('countPerDayHistoryLoading', { count: v }),
    use_one_unloading_value: (v) => (v ? t('useOneValueForUnloadingHistory') : t('noUseOneValueForUnloadingHistory')),
    use_one_loading_value: (v) => (v ? t('useOneValueForLoadingHistory') : t('noUseOneValueForLoadingHistory')),
  }

  const formattedChanges = Object.entries(changes).map(([key, value]) => {
    const formater = formatters[key]
    if (formater) {
      return formater(value)
    } else {
      return `${key}: ${value.toString()}`
    }
  })

  return (
    <div>
      {formattedChanges.map((entry, index) => (
        <Fragment key={index}>
          {entry}
          {index < formattedChanges.length - 1 ? ', ' : ''}
        </Fragment>
      ))}
    </div>
  )
}
